import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PromptDialog from "./PromptDialog";

interface PasswordConfirmationDialogProps {
  onCancel: () => void;
  onConfirm: (password: string) => void | Promise<void>;
  isLoading?: boolean;
  title?: string;
  message?: string | React.ReactNode;
}

const PasswordConfirmationDialog: React.FC<PasswordConfirmationDialogProps> = ({
  onCancel,
  onConfirm,
  isLoading = false,
  title,
  message,
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) setError("");
  };

  const handleConfirm = async () => {
    if (!password.trim()) {
      setError("パスワードを入力してください");
      return;
    }

    await onConfirm(password);
  };

  // Default message explaining the class deletion consequences
  const defaultMessage = (
    <div className="space-y-3">
      <p className="whitespace-pre-wrap">
        {t("_class.deletion_warning_1", {
          defaultValue:
            "クラスを削除すると、該当クラスに登録されている園児情報や写真データがすべて削除され、復元することができません。\n園児と紐づいている保護者アカウントが存在する場合、削除できません。",
        })}
      </p>
      <p>
        {t("_class.deletion_warning_3", {
          defaultValue:
            "進級・卒園・園児データ削除は別画面から行ってください。",
        })}
      </p>
      <p className="pt-2">
        {t("_class.deletion_proceed_instructions", {
          defaultValue:
            "削除処理に進む場合は施設のパスワードを入力し、「同意して進む」を選択してください。",
        })}
      </p>
    </div>
  );

  return (
    <PromptDialog
      title={
        title || t("_class.delete_class", { defaultValue: "クラスを削除する" })
      }
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmText={t("_class.agree_and_proceed", {
        defaultValue: "同意して進む",
      })}
      cancelText={t("_generic.cancel", { defaultValue: "キャンセル" })}
      isLoading={isLoading}
    >
      <div className="space-y-4">
        {message || defaultMessage}

        <div className="mt-6">
          <div className="relative">
            <input
              type="password"
              className={`w-full px-3 py-2 border ${
                error ? "border-red-500" : "border-gray-300"
              } rounded focus:outline-none focus:ring-1 focus:ring-blue-500`}
              placeholder={t("_generic.password_halfwidth_alphanumeric", {
                defaultValue: "パスワード　半角英数字8文字以上",
              })}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        </div>
      </div>
    </PromptDialog>
  );
};

export default PasswordConfirmationDialog;
